import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

export type YenUnit = '1' | '1000' | '1000000';

export interface YenUnitToggleProps {
  value: YenUnit;
  onChange: (next: YenUnit) => void;
  /**
   * Workspace reporting currency. The toggle is JPY-only and hides itself
   * for every other currency — Japanese 千円 / 百万円 don't translate to
   * USD or EUR amounts.
   */
  currency: string;
  className?: string;
}

const OPTIONS: ReadonlyArray<{
  value: YenUnit;
  labelKey: string;
  glyph: string;
}> = [
  { value: '1', labelKey: 'common.unit_yen', glyph: '¥' },
  { value: '1000', labelKey: 'common.unit_thousand_yen', glyph: '千円' },
  { value: '1000000', labelKey: 'common.unit_million_yen', glyph: '百万円' },
];

/**
 * Tri-state segmented control for picking the display unit on a JPY
 * amount: 1 / 1,000 / 1,000,000. Renders nothing when the workspace
 * currency isn't JPY — call sites can mount it unconditionally and
 * the component decides whether to show itself.
 */
export function YenUnitToggle({
  value,
  onChange,
  currency,
  className,
}: YenUnitToggleProps) {
  const { t } = useTranslation();

  if ((currency || '').toUpperCase() !== 'JPY') return null;

  return (
    <div
      role="radiogroup"
      aria-label={t('common.amount_unit', 'amount unit')}
      title={t('common.amount_unit', 'amount unit')}
      className={cn(
        'inline-flex items-center rounded-md border border-ink-200 bg-white p-0.5 text-xs',
        className,
      )}
    >
      {OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'px-2 py-1 rounded-sm transition-colors',
              active
                ? 'bg-accent-500 text-white font-medium'
                : 'text-ink-600 hover:bg-ink-100',
            )}
          >
            <span aria-hidden>{opt.glyph}</span>
            <span className="sr-only">{t(opt.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}
